import React, { useEffect, useMemo, useState } from "react";
import API from "../../../util/API";
import {
  Upload,
  Image as ImageIcon,
  Film,
  FileText,
  Trash2,
  Copy,
  Grid2X2,
  List,
  Search,
  RefreshCw,
  Download,
  FolderPlus,
  Link as LinkIcon,
  X,
} from "lucide-react";

export default function FileMediaManager() {
  const [items, setItems] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [folders, setFolders] = useState(["Marketing", "Product", "Factory"]);
  const [folderName, setFolderName] = useState("");
  const [publicCDN, setPublicCDN] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [viewMode, setViewMode] = useState("grid");
  const [selected, setSelected] = useState([]);

  const loadMedia = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await API.get("/media");
      setItems(res?.data?.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load media");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMedia();
  }, []);

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) return;
    setUploading(true);
    setError("");
    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => formData.append("files", file));
      await API.post("/media/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSelectedFiles([]);
      await loadMedia();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to upload files");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (name) => {
    if (!name) return;
    const confirmed = window.confirm("Delete this file? This action cannot be undone.");
    if (!confirmed) return;
    try {
      await API.delete(`/media/${encodeURIComponent(name)}`);
      setSelected((prev) => prev.filter((id) => id !== name));
      await loadMedia();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to delete file");
    }
  };

  const handleCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // ignore
    }
  };

  const totalSize = useMemo(() => items.reduce((sum, i) => sum + (Number(i.size) || 0), 0), [items]);
  const newest = useMemo(() => items[0]?.updatedAt ? new Date(items[0].updatedAt) : null, [items]);

  const filtered = useMemo(() => {
    const q = String(query || "").toLowerCase().trim();
    return items.filter((item) => {
      const name = String(item.name || "").toLowerCase();
      if (q && !name.includes(q)) return false;
      if (typeFilter === "images") return /\.(jpg|jpeg|png|webp|gif)$/i.test(name);
      if (typeFilter === "videos") return /\.(mp4|mov|avi|webm)$/i.test(name);
      if (typeFilter === "docs") return /\.(pdf|doc|docx|xls|xlsx|csv)$/i.test(name);
      return true;
    });
  }, [items, query, typeFilter]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    if (sortBy === "name") list.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    if (sortBy === "size") list.sort((a, b) => Number(b.size || 0) - Number(a.size || 0));
    if (sortBy === "oldest") list.sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt));
    if (sortBy === "newest") list.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    return list;
  }, [filtered, sortBy]);

  const toggleSelect = (name) => {
    setSelected((prev) => prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]);
  };

  return (
    <div className="p-6 space-y-6" style={{ fontFamily: "'Space Grotesk', 'Segoe UI', sans-serif" }}>
      <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-amber-50 p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.28em] text-slate-400">Media Center</p>
            <h1 className="mt-2 text-2xl md:text-3xl font-black tracking-tight text-slate-900">
              File & Media Manager
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Upload, organize, and manage assets used across the storefront and admin content.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={loadMedia}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <RefreshCw size={16} /> Refresh
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Total Files</p>
            <p className="mt-2 text-2xl font-black text-slate-900">{items.length}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Total Size</p>
            <p className="mt-2 text-2xl font-black text-slate-900">
              {(totalSize / (1024 * 1024)).toFixed(1)} MB
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Latest Upload</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">
              {newest ? newest.toLocaleString() : "—"}
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">CDN Access</p>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPublicCDN(!publicCDN)}
                className={
                  "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold " +
                  (publicCDN ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600")
                }
              >
                {publicCDN ? "Public" : "Private"}
              </button>
              <span className="text-xs text-slate-500">Toggle sharing</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 space-y-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2">
              <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" />
              <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Upload Assets</h2>
            </div>
            <p className="mt-2 text-xs text-slate-500">JPG, PNG, WEBP, MP4, PDF. Max 10MB each.</p>

            <label className="mt-4 flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
              <Upload className="w-6 h-6 text-slate-500" />
              <span className="text-sm font-semibold text-slate-700">Drop files here or browse</span>
              <span className="text-xs text-slate-500">Up to 10 files at once</span>
              <input type="file" className="hidden" onChange={handleFileUpload} multiple />
            </label>

            {selectedFiles.length ? (
              <div className="mt-4 space-y-2">
                {selectedFiles.map((file) => (
                  <div key={file.name} className="flex items-center justify-between text-xs text-slate-600">
                    <span className="truncate">{file.name}</span>
                    <span>{(file.size / 1024).toFixed(0)} KB</span>
                  </div>
                ))}
              </div>
            ) : null}

            {error ? <p className="text-sm text-rose-600 mt-3">{error}</p> : null}
            <button
              className="mt-4 w-full rounded-2xl bg-slate-900 text-white py-2 text-sm font-semibold disabled:opacity-60"
              onClick={handleUpload}
              disabled={uploading || !selectedFiles.length}
            >
              {uploading ? "Uploading..." : "Upload Files"}
            </button>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2">
              <FolderPlus className="w-4 h-4 text-slate-600" />
              <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Folders</h2>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {folders.map((folder) => (
                <span key={folder} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
                  {folder}
                </span>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <input
                type="text"
                className="flex-1 rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                placeholder="New folder name"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
              />
              <button
                className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold"
                onClick={() => {
                  if (!folderName.trim()) return;
                  setFolders([...folders, folderName]);
                  setFolderName("");
                }}
              >
                Add
              </button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-8 space-y-5">
          <div className="rounded-3xl border border-slate-200 bg-white p-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search files by name..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-10 py-2 text-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold"
                >
                  <option value="all">All types</option>
                  <option value="images">Images</option>
                  <option value="videos">Videos</option>
                  <option value="docs">Documents</option>
                </select>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold"
                >
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="name">Name</option>
                  <option value="size">Size</option>
                </select>
                <button
                  type="button"
                  onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
                  className="h-10 w-10 rounded-2xl border border-slate-200 bg-white flex items-center justify-center"
                >
                  {viewMode === "grid" ? <List size={16} /> : <Grid2X2 size={16} />}
                </button>
              </div>
            </div>

            {selected.length ? (
              <div className="mt-3 flex items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
                <span className="font-semibold text-amber-900">{selected.length} selected</span>
                <div className="flex items-center gap-2">
                  <button className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white px-3 py-1 text-xs font-semibold">
                    <Download size={14} /> Download
                  </button>
                  <button
                    onClick={() => setSelected([])}
                    className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white px-3 py-1 text-xs font-semibold"
                  >
                    <X size={14} /> Clear
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5">
            {loading ? (
              <div className="text-sm text-slate-500">Loading media…</div>
            ) : sorted.length === 0 ? (
              <div className="text-sm text-slate-500">No media found.</div>
            ) : viewMode === "grid" ? (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {sorted.map((item) => (
                  <div key={item.name} className="rounded-2xl border border-slate-200 overflow-hidden group">
                    <div className="relative">
                      <img src={item.url} alt={item.name} className="h-36 w-full object-cover" />
                      <button
                        className={
                          "absolute top-2 left-2 h-8 w-8 rounded-full border bg-white/90 flex items-center justify-center " +
                          (selected.includes(item.name) ? "border-slate-900" : "border-slate-200")
                        }
                        onClick={() => toggleSelect(item.name)}
                      >
                        {selected.includes(item.name) ? "✓" : ""}
                      </button>
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-semibold text-slate-900 truncate">{item.name}</p>
                      <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                        <span>{(item.size / 1024).toFixed(0)} KB</span>
                        <span>•</span>
                        <span>{new Date(item.updatedAt).toLocaleDateString()}</span>
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <button className="h-8 w-8 rounded-full border border-slate-200 flex items-center justify-center" onClick={() => handleCopy(item.url)}>
                          <Copy size={14} />
                        </button>
                        <a className="h-8 w-8 rounded-full border border-slate-200 flex items-center justify-center" href={item.url} target="_blank" rel="noreferrer">
                          <LinkIcon size={14} />
                        </a>
                        <button className="h-8 w-8 rounded-full border border-rose-200 text-rose-600 flex items-center justify-center" onClick={() => handleDelete(item.name)}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {sorted.map((item) => (
                  <div key={item.name} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 p-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <button
                        className={
                          "h-8 w-8 rounded-full border flex items-center justify-center " +
                          (selected.includes(item.name) ? "border-slate-900" : "border-slate-200")
                        }
                        onClick={() => toggleSelect(item.name)}
                      >
                        {selected.includes(item.name) ? "✓" : ""}
                      </button>
                      <div className="h-12 w-12 rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center">
                        {/\.(mp4|mov|avi|webm)$/i.test(item.name) ? <Film size={16} /> : /\.(pdf|doc|docx|xls|xlsx|csv)$/i.test(item.name) ? <FileText size={16} /> : <ImageIcon size={16} />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{item.name}</p>
                        <p className="text-xs text-slate-500">{(item.size / 1024).toFixed(0)} KB • {new Date(item.updatedAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="h-8 w-8 rounded-full border border-slate-200 flex items-center justify-center" onClick={() => handleCopy(item.url)}>
                        <Copy size={14} />
                      </button>
                      <a className="h-8 w-8 rounded-full border border-slate-200 flex items-center justify-center" href={item.url} target="_blank" rel="noreferrer">
                        <LinkIcon size={14} />
                      </a>
                      <button className="h-8 w-8 rounded-full border border-rose-200 text-rose-600 flex items-center justify-center" onClick={() => handleDelete(item.name)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
